import { FC, useState } from 'react';
import isEqual from 'react-fast-compare';
import Header from '../../../common/Header/Header';
import UnsavedChangesModal from '../../../common/UnsavedChangesModal/UnsavedChangesModal';
import { showToast } from '../../../components/Toast/Toast';
import { ToastTypes } from '../../../components/Toast/Toast.types';
import { BuildRequestBodyParams } from '../../../components/TreeView/TreeView.types';
import useTreeViewRowStates from '../../../components/TreeView/useTreeViewRowStates';
import { useGetRecipeBases } from '../../../services/base/base.service';
import { useGetRecipeProducts } from '../../../services/product/product.service';
import { useCopyRecipes } from '../../../services/recipe/recipe.service';
import { useGetRecipeSizes } from '../../../services/size/size.service';
import { fetchSpecsInRecipe, useSpecsInRecipe } from '../../../services/spec/spec.service';
import { genericErrorShowToastOptions } from '../../../utils/useGenericErrorMessage/useGenericErrorMessage';
import SelectRecipes from '../AddRawProds/SelectRecipes/SelectRecipes';
import Confirmation from './Confirmation/Confirmation';
import ReviewChanges from './ReviewChanges/ReviewChanges';
import UpdateRecipeDetails from './UpdateRecipeDetails/UpdateRecipeDetails';
import {
  CopyRecipeFormValues,
  DescriptionUpdateRule,
  FprodUpdateRule,
} from './UpdateRecipeDetails/UpdateRecipeDetails.types';

const CopyRecipe: FC = () => {
  const [step, setStep] = useState(0);
  const [selectedRecipes, setSelectedRecipes] = useState<any[]>([]);
  const [formValues, setFormValues] = useState<CopyRecipeFormValues>({
    description: '',
    longDescription: '',
    fmsProdNo: '',
    fprodUpdateRule: FprodUpdateRule.NO_ACTION,
    descriptionUpdateRule: DescriptionUpdateRule.NO_ACTION,
  });
  const [formErrors, setFormErrors] = useState<Omit<CopyRecipeFormValues, 'fprodUpdateRule' | 'descriptionUpdateRule'>>(
    {
      description: '',
      longDescription: '',
      fmsProdNo: '',
    }
  );

  const { mutate, data, isLoading } = useCopyRecipes();

  const dataStructure = [
    {
      fetchChildren: useGetRecipeBases,
      idKey: 'classNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.row.classNo };
      },
    },
    {
      fetchChildren: useGetRecipeSizes,
      idKey: 'baseNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return { classNo: params.parentId, baseNo: params.row.baseNo };
      },
    },
    {
      fetchChildren: useGetRecipeProducts,
      idKey: 'sizeNo',
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      showCheckboxes: true,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentId,
          sizeNo: params.row.sizeNo,
        };
      },
    },
    {
      fetchChildren: useSpecsInRecipe,
      idKey: 'productNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => params.row.description,
      buildRequestBody: (params: BuildRequestBodyParams) => {
        return {
          classNo: params.parentRows?.[0].id,
          baseNo: params.parentRows?.[1].id,
          sizeNo: params.parentId,
          productNo: params.row.productNo,
        };
      },
    },
    {
      to: () => '',
      idKey: 'specNo',
      showCheckboxes: true,
      buildLabel: (params: BuildRequestBodyParams) => `${params.row.specNo} - ${params.row.description}`,
    },
  ];

  const { checkedRows, expandedRows, handleRowClick, handleCheckboxClick } = useTreeViewRowStates(
    dataStructure,
    'selectRecipesToCopy',
    true
  );

  const mapParentRowsToFilters = (parentRows: { id: string; level: number }[]) => {
    return {
      classNo: parentRows[0]?.id,
      baseNo: parentRows[1]?.id,
      sizeNo: parentRows[2]?.id,
      productNo: parentRows[3]?.id,
    };
  };

  const handleCheckboxClickInternal = (row: any) => {
    if (row.rowChecked) {
      if (row.level === 4) {
        // recipe row was checked
        setSelectedRecipes((selectedRecipes) => {
          const selectedRecipesWithCheckedRowAdded = [...selectedRecipes, row];
          const onlyUniqueValues = selectedRecipesWithCheckedRowAdded.filter((spec, index) => {
            const firstIndex = selectedRecipesWithCheckedRowAdded.findIndex((val) => val.specNo === spec.specNo);
            return index === firstIndex;
          });
          const newSelectedRecipes = onlyUniqueValues.map((recipe) => {
            return {
              ...recipe,
              rowChecked: true,
              level: 4,
              parentRows: [
                { level: 0, id: recipe.moaClassNo },
                { level: 1, id: recipe.moaBaseNo },
                { level: 2, id: recipe.moaSizeNo },
                { level: 3, id: recipe.moaProductNo },
                { level: 4, id: recipe.specNo },
              ],
            };
          });
          return newSelectedRecipes;
        });
      } else {
        // class / base / size / prod row was checked
        const filters = mapParentRowsToFilters(row.parentRows);
        // fetch the specs that correspond with that row and add them to selectedRecipes (if not already there)
        fetchSpecsInRecipe(
          filters,
          (response) => {
            setSelectedRecipes((selectedRecipes) => {
              const selectedRecipesWithResponseDataAdded = [...selectedRecipes, ...response.data];
              const onlyUniqueValues = selectedRecipesWithResponseDataAdded.filter((spec, index) => {
                const firstIndex = selectedRecipesWithResponseDataAdded.findIndex((val) => val.specNo === spec.specNo);
                return index === firstIndex;
              });
              const newSelectedRecipes = onlyUniqueValues.map((recipe) => {
                return {
                  ...recipe,
                  rowChecked: true,
                  level: 4,
                  parentRows: [
                    { level: 0, id: recipe.moaClassNo },
                    { level: 1, id: recipe.moaBaseNo },
                    { level: 2, id: recipe.moaSizeNo },
                    { level: 3, id: recipe.moaProductNo },
                    { level: 4, id: recipe.specNo },
                  ],
                };
              });
              return newSelectedRecipes;
            });
          },
          (error) => {
            showToast({
              ...genericErrorShowToastOptions,
              label: 'Error fetching specs',
              toastType: ToastTypes.Error,
              id: 'errorFetchingSpecsInRecipe',
            });
          }
        );
      }
    } else {
      // row was just unchecked
      setSelectedRecipes((selectedRecipes) => {
        // filter through selectedRecipes and remove all the recipes that correspond with that row
        return selectedRecipes.filter((recipe) => {
          if (
            !isEqual(
              row.parentRows,
              recipe.parentRows.filter((parentRow: any) => parentRow.level <= row.level)
            )
          ) {
            return true;
          } else {
            return false;
          }
        });
      });
    }
    // call the fn from useTreeViewRowStates to mark the row as checked/unchecked there
    handleCheckboxClick(row);
  };

  const renderStep = () => {
    switch (step) {
      default:
        return (
          <SelectRecipes
            flow='copy'
            selectedRecipes={selectedRecipes}
            setStep={setStep}
            checkedRows={checkedRows}
            expandedRows={expandedRows}
            handleRowClick={handleRowClick}
            handleCheckboxClick={handleCheckboxClickInternal}
            dataStructure={dataStructure}
            setSelectedRecipes={setSelectedRecipes}
          />
        );
      case 1:
        return (
          <UpdateRecipeDetails
            selectedRecipes={selectedRecipes}
            setStep={setStep}
            formValues={formValues}
            setFormValues={setFormValues}
            formErrors={formErrors}
            setFormErrors={setFormErrors}
          />
        );
      case 2:
        return (
          <ReviewChanges
            selectedRecipes={selectedRecipes}
            setStep={setStep}
            formValues={formValues}
            mutate={mutate}
            isLoading={isLoading}
          />
        );
      case 3:
        return <Confirmation data={data?.data} />;
    }
  };

  return (
    <>
      <Header
        breadcrumb={{ label: 'Back to Recipes', to: '/recipes' }}
        title='Copy Recipes'
        stepTracker={
          step !== 3
            ? {
                currentStep: step,
                steps: [
                  {
                    label: 'Select Recipes',
                    onClick: () => {
                      setFormErrors({ description: '', longDescription: '', fmsProdNo: '' });
                      setStep(0);
                    },
                  },
                  { label: 'Set Description', onClick: () => setStep(1) },
                  { label: 'Review' },
                ],
              }
            : undefined
        }
      />
      {renderStep()}
      <UnsavedChangesModal unsavedChangesExist={step === 1 || step === 2} />
    </>
  );
};

export default CopyRecipe;
